import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, firstValueFrom, map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IApiResponse } from '../utils/common.interface';
import { formatEther } from 'viem';

export interface AdminConfiguredStorageFee {
  category: string;
  location: string;
  fee?: number | string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ExchangeService {
  /*
   * Marketplace-level in-memory cache for admin-configured storage-fee mappings.
   *
   * This avoids refetching the same config on every component load and keeps
   * category+location checks consistent across dashboard, collection, wallet, and detail pages.
   */
  private adminConfiguredStorageFees = new BehaviorSubject<AdminConfiguredStorageFee[]>([]);

  /*
   * Single in-flight request shared across concurrent callers. Without this, every
   * view that loads storage-fee config on init would fire its own request (and its own
   * retry loop) while the cache is still empty, multiplying traffic during an outage.
   */
  private adminFeesRequest$?: Observable<AdminConfiguredStorageFee[]>;

  /*
   * Whether a storage-fee config fetch has ever succeeded. Distinguishes a genuinely
   * empty config (admin configured no fees) from "not loaded yet", so callers can avoid
   * making custody-lock decisions on an unpopulated config.
   */
  private adminFeesLoaded = false;

  constructor(
    private http: HttpClient
  ) { }

  public exchangeOrder(params: any) {
    return this.http.post(`${environment.API_BASE_URL_V3}/exchange-nft`, params);
  }

  public updateSellOrder(orderId: string, params: any) {
    return this.http.patch(`${environment.API_BASE_URL_V3}/exchange-nft?id=${orderId}`, params);
  }

  public createBid(params: any) {
    return this.http.post(`${environment.API_BASE_URL_V3}/nft-bid`, params);
  }

  public updateBid(bidId: string, params: any) {
    return this.http.patch(`${environment.API_BASE_URL_V3}/nft-bid?id=${bidId}`, params);
  }

  public cancelBid(bidId: string, params: any) {
    return this.http.patch(`${environment.API_BASE_URL_V3}/nft-bid?id=${bidId}`, params);
  }

  public acceptBid(bidId: string, txData: any) {
    const params = new URLSearchParams({
      id: bidId,
      transaction_hash: txData.transactionHash,
      gas_price: formatEther(txData?.effectiveGasPrice?.toString()),
      block_number: `${txData?.blockNumber}`,
    }).toString();
    return this.http.patch(`${environment.API_BASE_URL_V3}/nft-bid-accept?${params}`, {});
  }

  /**
   * get bids
   * @param {string} nftId
   * @param {number} page
   * @param {number} limit
   */
  public getBids(nftId: string, page: number = 1, limit: number = 5) {
    return this.http.get<IApiResponse>(`${environment.API_BASE_URL_V3}/nft-bids?nft=${nftId}&page=${page}&limit=${limit}`);
  }

  public getBid(bidId: string) {
    return firstValueFrom(this.http.get(`${environment.API_BASE_URL_V3}/nft-bid-status?id=${bidId}`));
  }

  async getSignature(params: any) {
    try {
      const result = firstValueFrom(this.http.post(`${environment.API_BASE_URL_V3}/nft-bid-signature`, params));
      return result;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }

  /**
   * Gets storage fee details
   * @param location
   * @param category
   * @returns
   */
  getStorageFeeDetails(location: string, category: string) {
    const params = new URLSearchParams({
      location: String(location || ''),
      category: String(category || '')
    }).toString();
    return this.http.get(`${environment.API_BASE_URL_V3}/storage-fees?${params}`)
  }

  /**
   * Returns all admin configured storage fee mappings and caches them in marketplace scope.
   */
  getAdminConfiguredStorageFees(forceRefresh: boolean = false): Observable<AdminConfiguredStorageFee[]> {
    /*
     * Return cached data unless force refresh is requested.
     *
     * This endpoint changes infrequently and is shared by multiple views, so
     * caching reduces repeated API calls during navigation.
     */
    const cached = this.adminConfiguredStorageFees.value;
    if (!forceRefresh && this.adminFeesLoaded) {
      return of(cached);
    }

    // Reuse an already in-flight request so concurrent callers share one HTTP call
    // (and one retry loop) instead of each starting their own.
    if (!forceRefresh && this.adminFeesRequest$) {
      return this.adminFeesRequest$;
    }

    // Transient failures (network / 408 / 502 / 503) are retried globally by
    // RetryInterceptor, which generalises the predicate this call used to hold locally.
    // Retrying matters here because an empty storage-fee config leaves custody-lock
    // gating fail-open.
    this.adminFeesRequest$ = this.http.get(`${environment.API_BASE_URL_V3}/admin-configured-storage-fees`).pipe(
      map((response: any) => {
        const configs = Array.isArray(response?.data) ? response.data : [];
        return configs
          .map((config: any) => ({
            category: String(config?.category || '').trim().toLowerCase(),
            location: String(config?.location || '').trim().toLowerCase(),
            fee: config?.fee ?? config?.storage_fee ?? config?.storage_fee_amount ?? config?.fee_amount ?? null
          }))
          .filter((config: AdminConfiguredStorageFee) => config.category && config.location);
      }),
      tap((configs: AdminConfiguredStorageFee[]) => {
        this.adminFeesLoaded = true;
        this.adminConfiguredStorageFees.next(configs);
      }),
      // Clear the shared reference once settled so a later cache-miss can refetch.
      finalize(() => {
        this.adminFeesRequest$ = undefined;
      }),
      shareReplay(1)
    );
    return this.adminFeesRequest$;
  }

  getCachedAdminConfiguredStorageFees(): AdminConfiguredStorageFee[] {
    return this.adminConfiguredStorageFees.value;
  }

  /**
   * Whether a storage-fee config fetch has ever succeeded. Callers use this to avoid
   * making custody-lock decisions against a not-yet-loaded config.
   */
  isAdminConfiguredStorageFeesLoaded(): boolean {
    return this.adminFeesLoaded;
  }

  /**
   * Returns exchange service
   * @returns
   */
  getStorageFeeDayList() {
    return this.http.get(`${environment.API_BASE_URL_V3}/storage-fee-days`)
  }


  /**
   * Updates storagefee details
   * @param nft_id
   * @param request
   * @returns
   */
  updateStoragefeeDetails(nft_id: any, request: any) {
    return this.http.patch(`${environment.API_BASE_URL_V3}/nft/expired-date?id=${nft_id}`, request)
  }

  /** Bulk custody-fee sync: one call for all NFTs paid in a single payNftCustodyFee tx. */
  updateBulkStoragefeeDetails(request: any) {
    return this.http.patch(`${environment.API_BASE_URL_V3}/nft/expired-dates`, request)
  }

  /**
   * Creates storage fee signature
   * @param request
   * @returns
   */
  async createStorageFeeSignature(request: { [key: string]: any }) {
    try {
      const result = await this.http.post(`${environment.API_BASE_URL_V3}/fire-block/storage-payment-signature`, request).toPromise();
      return result;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }

  }

  /**
   * Updates completed status
   * @param {any} payload - payload to update completed status
   * @returns {Observable<any>} - observable of updated completed status
   */
  updateCompletedStatus(payload: any) {
    return this.http.patch(`${environment.API_BASE_URL_V3}/bundle-purchase/update-completed-status`, payload);
  }
}
